import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import {
  bindingOccurrences,
  locationLabel,
} from '@/pages/Editor/components/Composition/compositionPresentation.ts';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Code, Group, Stack, Text } from '@mantine/core';
import axios from 'axios';
import {
  useCompositionRecord,
  useCompositionReadiness,
  useCompositionScope,
} from '@/hooks/api/automations/useExecutionCompositions.ts';
import {
  usePublicationMutations,
  usePublicationDocuments,
} from '@/hooks/api/automations/useAutomationPublications.ts';
import type { PublicationPreview, PublicationTestStatus } from '@/types/automationPublications.ts';
import type { Fence } from '@/types/executionCompositions.ts';
import {
  ExactDetails,
  ImplementationLabel,
  ReleaseSection,
  SuiteLink,
  TriggerLabel,
} from '@/pages/Editor/components/Composition/CompositionPresentation.tsx';
import {
  PendingPublication,
  publicationRecoveryKey,
  readPublicationRecovery,
  retainPublication,
} from './publicationRecovery.ts';
import {
  publicationTestStatusAcknowledgement,
  publicationTestStatusSummary,
} from './publicationTestStatus.ts';
import ReleaseDocumentationReview from './ReleaseDocumentationReview.tsx';

type Props = {
  releaseId: string;
  canEdit: boolean;
  automationIds?: string[];
  action?: 'activate' | 'rollback';
  onCompleted?: () => void;
  showFreezeAction?: boolean;
};
export default function PublicationPanel(props: Props) {
  const scope = useCompositionScope();
  return (
    <PublicationReview
      key={JSON.stringify([
        scope,
        props.releaseId,
        props.action ?? 'activate',
        props.automationIds,
      ])}
      {...props}
    />
  );
}
function PublicationReview({
  releaseId,
  canEdit,
  automationIds,
  action = 'activate',
  onCompleted,
  showFreezeAction = true,
}: Props) {
  const scope = useCompositionScope();
  const key = publicationRecoveryKey(scope, releaseId);
  const record = useCompositionRecord(releaseId);
  const readiness = useCompositionReadiness(releaseId);
  const { freeze, preview, confirm } = usePublicationMutations();
  const [selected, setSelected] = useState<string[]>(automationIds ?? []);
  const [pending, setPending] = useState<PendingPublication | null>(() =>
    readPublicationRecovery(key, releaseId),
  );
  const [review, setReview] = useState<PublicationPreview | null>(pending?.preview ?? null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [testStatusAcknowledged, setTestStatusAcknowledged] = useState(false);
  const [reviewTestStatus, setReviewTestStatus] = useState<
    | {
        aggregate: Omit<
          PublicationTestStatus,
          | 'automation_id'
          | 'automation_version_id'
          | 'suite_id'
          | 'suite_digest'
          | 'detail_reference'
          | 'message'
        >;
        versions: PublicationTestStatus[];
      }
    | undefined
  >();
  const [showDocuments, setShowDocuments] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!review) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(0, Date.parse(review.plan.expires_at) - Date.now() + 1),
    );
    return () => window.clearTimeout(timer);
  }, [review]);
  const submitting = useRef(false);
  const busy = freeze.isPending || preview.isPending || confirm.isPending;
  const candidate = !record.isError && record.isFetchedAfterMount ? record.data : undefined;
  const proof = !readiness.isError && readiness.isFetchedAfterMount ? readiness.data : undefined;
  useEffect(() => {
    if (
      [record.error, readiness.error].some(
        (cause) => axios.isAxiosError(cause) && [401, 403].includes(cause.response?.status ?? 0),
      )
    ) {
      setPending(null);
      setReview(null);
      setAcknowledged(false);
      retainPublication(key, null);
    }
  }, [record.error, readiness.error, key]);
  const fresh = Boolean(
    candidate &&
    proof &&
    !record.isError &&
    !readiness.isError &&
    !record.isFetching &&
    !readiness.isFetching &&
    record.isFetchedAfterMount &&
    readiness.isFetchedAfterMount &&
    proof.lifecycle_revision === candidate.record.lifecycle_revision &&
    proof.composition_digest === candidate.record.identity.composition_digest &&
    proof.resource_digest === candidate.record.identity.resource_digest &&
    proof.documents.documentation_digest === candidate.record.identity.documentation_digest,
  );
  const reviewFresh = Boolean(
    fresh && review && candidate && sameFence(review.plan.release, candidate.record),
  );
  const reviewedAction = review?.plan.action ?? action;
  const hasBlockingFindings = Boolean(review?.plan.findings.some((finding) => finding.blocking));
  const testStatus = proof?.test_status;
  const displayedTestStatus = review ? reviewTestStatus : testStatus;
  const testsNeedAcknowledgement =
    !displayedTestStatus || displayedTestStatus.aggregate.overall_status !== 'passing';
  const confirmationDocumentFences = review
    ? review.plan.action === 'activate'
      ? [review.plan.release]
      : [
          ...new Map(
            review.plan.targets.flatMap((target) =>
              target.selected_release
                ? [[target.selected_release.release_id, target.selected_release] as const]
                : [],
            ),
          ).values(),
        ]
    : [];
  const documentFences = [
    ...new Map(
      [...(candidate ? [candidate.record] : []), ...confirmationDocumentFences].map(
        (fence) => [fence.release_id, fence] as const,
      ),
    ).values(),
  ];
  const documents = usePublicationDocuments(documentFences, showDocuments);
  const documentResultByReleaseId = new Map(
    documentFences.map((fence, index) => [fence.release_id, documents[index]]),
  );
  const candidateDocumentResult = documentResultByReleaseId.get(releaseId);
  const documentsFresh =
    confirmationDocumentFences.length > 0 &&
    confirmationDocumentFences.every((fence) => {
      const result = documentResultByReleaseId.get(fence.release_id);
      return result?.data && !result.isError && !result.isFetching && result.isFetchedAfterMount;
    });
  const rootAutomations = [
    ...new Set(candidate?.candidate.roots.map((root) => root.automation_id) ?? []),
  ];
  const eligibleTargets = candidate?.candidate.suite
    ? rootAutomations.filter((id) =>
        candidate.candidate.suite?.members.some(
          (member) => member.automation_id === id && member.is_entrypoint,
        ),
      )
    : rootAutomations;
  let inspectedCalls: ReturnType<typeof bindingOccurrences> = [];
  let inspectionError = '';
  try {
    if (candidate) inspectedCalls = bindingOccurrences(candidate.candidate);
  } catch (cause) {
    inspectionError = errorMessage(cause);
  }
  const labels = new Map(
    candidate?.candidate.members.map((member) => [
      member.automation_id,
      member.automation_version_id,
    ]) ?? [],
  );
  const retain = (next: PendingPublication | null) => {
    setPending(next);
    retainPublication(key, next);
  };
  const clearReview = () => {
    setReview(null);
    setAcknowledged(false);
    setTestStatusAcknowledged(false);
    setReviewTestStatus(undefined);
    setError('');
    retain(null);
  };
  const reviewPublication = async () => {
    if (busy || pending?.submitted || !canEdit || !fresh || !selected.length || inspectionError)
      return;
    clearReview();
    setSuccess('');
    try {
      const next = await preview.mutateAsync({
        releaseId,
        automationIds: selected,
        action,
      });
      if (
        !sameFence(next.plan.release, candidate!.record) ||
        (action === 'activate' &&
          next.plan.targets.some((target) => {
            const roots = candidate!.candidate.roots.filter(
              (root) => root.automation_id === target.before.automation_id,
            );
            return (
              target.roots.length !== roots.length ||
              target.roots.some(
                (root) =>
                  !roots.some(
                    (expected) =>
                      expected.automation_id === root.automation_id &&
                      expected.automation_version_id === root.automation_version_id &&
                      expected.trigger_step_id === root.trigger_step_id &&
                      expected.binding_id === root.binding_id,
                  ),
              )
            );
          }))
      ) {
        throw new Error('Candidate changed during review. Refresh it and review again.');
      }
      setTestStatusAcknowledged(false);
      setReviewTestStatus(testStatus);
      setReview(next);
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };
  const submit = async () => {
    if (submitting.current || !canEdit) return;
    const recovering = pending?.submitted === true;
    if (
      !recovering &&
      (!review ||
        hasBlockingFindings ||
        !reviewFresh ||
        Date.parse(review.plan.expires_at) <= Date.now() ||
        (review.plan.document_confirmation_required && (!acknowledged || !documentsFresh)) ||
        (testsNeedAcknowledgement && !testStatusAcknowledged))
    )
      return;
    const next = recovering
      ? pending!
      : {
          preview: review!,
          submitted: true,
          request: {
            schema_version: 'automation_activation_apply.v1' as const,
            request_id: crypto.randomUUID(),
            preview_token: review!.preview_token,
            preview_digest: review!.preview_digest,
            confirm_documentation: acknowledged,
          },
        };
    submitting.current = true;
    retain(next);
    setError('');
    try {
      const result = await confirm.mutateAsync(next.request);
      const ids = next.preview.plan.targets.map((target) => target.before.automation_id);
      if (
        result.release_id !== releaseId ||
        result.activations.length !== ids.length ||
        new Set(result.activations.map((item) => item.automation_id)).size !== ids.length ||
        result.activations.some((item) => !ids.includes(item.automation_id))
      )
        throw new Error(
          'Publication receipt does not confirm the reviewed targets. Retry the identical request.',
        );
      retain(null);
      setReview(null);
      setAcknowledged(false);
      setTestStatusAcknowledged(false);
      setReviewTestStatus(undefined);
      setSuccess(`Publication recorded: ${result.publication_id}`);
      onCompleted?.();
    } catch (cause) {
      const status = axios.isAxiosError(cause) ? cause.response?.status : undefined;
      if (status && status >= 400 && status < 500) {
        retain(null);
        setReview(null);
        setAcknowledged(false);
        setTestStatusAcknowledged(false);
        setReviewTestStatus(undefined);
      }
      setError(errorMessage(cause));
    } finally {
      submitting.current = false;
    }
  };
  return (
    <ReleaseSection
      title={reviewedAction === 'rollback' ? 'Restore' : 'Publish'}
      description={
        reviewedAction === 'rollback'
          ? 'Review the current publication and the state it will restore.'
          : 'Review the saved candidate and explicitly choose the Automation entrypoints to publish.'
      }
    >
      <Text fw={600}>
        {reviewedAction === 'rollback' ? 'Review restore' : 'Publish exact composition'}
      </Text>
      <Text size="sm">
        Publication changes only the selected Automation entrypoints. Dependencies are not activated
        separately.
      </Text>
      {!canEdit && (
        <Text size="sm">This view is read-only. Publication changes require edit access.</Text>
      )}
      <Button
        variant="subtle"
        disabled={busy}
        onClick={() => {
          if (!pending?.submitted) clearReview();
          void record.refetch();
          void readiness.refetch();
        }}
      >
        Refresh candidate and evidence
      </Button>
      {record.isError || readiness.isError ? (
        <Alert color="red">
          Publication context unavailable.{' '}
          <Button
            variant="subtle"
            onClick={() => {
              void record.refetch();
              void readiness.refetch();
            }}
          >
            Refresh
          </Button>
        </Alert>
      ) : null}
      {!candidate && !record.isError && <Text>Loading publication context…</Text>}
      {inspectionError && (
        <Alert color="red">Exact binding inspection unavailable: {inspectionError}</Alert>
      )}
      {candidate && (
        <>
          <Text size="sm">Saved release configuration · {candidate.status}</Text>
          <ExactDetails values={{ 'Release configuration ID': releaseId }} />
          <Text fw={600}>
            {reviewedAction === 'rollback' ? 'Entrypoints to restore' : 'Entrypoints to publish'}
          </Text>
          {!automationIds &&
            eligibleTargets.map((id) => (
              <Checkbox
                key={id}
                checked={selected.includes(id)}
                disabled={busy || Boolean(pending?.submitted) || !canEdit}
                label={
                  <>
                    <ImplementationLabel
                      version={{
                        automation_id: id,
                        automation_version_id: labels.get(id) ?? '',
                      }}
                    />
                    {' · '}
                    {id}
                  </>
                }
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  clearReview();
                  setSelected((previous) =>
                    checked ? [...previous, id] : previous.filter((value) => value !== id),
                  );
                }}
              />
            ))}
          {automationIds && (
            <Stack gap="xs">
              <Text fw={500}>Selected entrypoints</Text>
              {automationIds.map((id) => (
                <Text key={id} size="sm">
                  <ImplementationLabel
                    version={{
                      automation_id: id,
                      automation_version_id: labels.get(id) ?? '',
                    }}
                  />
                </Text>
              ))}
            </Stack>
          )}
          <details>
            <summary>Exact dependency bindings and members</summary>
            {candidate.candidate.members.map((member) => (
              <Text key={member.automation_version_id} size="sm">
                {member.automation_id} · {member.automation_version_id} ·{' '}
                {member.inclusion_reasons.join(', ')}
              </Text>
            ))}
            {inspectedCalls.map((call) => {
              const node = candidate.candidate.nodes.find(
                (item) => item.binding_id === call.bindingId,
              )!;
              return (
                <Text key={locationLabel(call.location)} size="sm">
                  {locationLabel(call.location)} → {node.automation_id} ·{' '}
                  {node.automation_version_id} · binding <Code>{node.binding_id}</Code>
                </Text>
              );
            })}
          </details>
          {proof && (
            <ReleaseDocumentationReview
              releaseId={releaseId}
              documents={proof.documents}
              detail={candidateDocumentResult?.data}
              showDocuments={showDocuments}
              loadingDocuments={candidateDocumentResult?.isFetching}
              documentsError={candidateDocumentResult?.error}
              onToggleDocuments={() => setShowDocuments((value) => !value)}
              onRetryDocuments={() => void candidateDocumentResult?.refetch()}
            />
          )}
          {proof?.blockers.map((message, index) => (
            <Alert key={index} color="orange">
              {message}
            </Alert>
          ))}
          {displayedTestStatus && (
            <Alert
              color={
                displayedTestStatus.aggregate.overall_status === 'passing' ? 'green' : 'yellow'
              }
              title="Release test status"
            >
              {publicationTestStatusSummary(displayedTestStatus.aggregate)}
              {displayedTestStatus.versions.length > 1 && (
                <Stack gap={2} mt="xs">
                  {displayedTestStatus.versions.map((status) => (
                    <Text key={status.automation_version_id} size="sm">
                      {status.automation_id} · {status.automation_version_id}: {status.overall_status.replaceAll('_', ' ')}
                      {' · '}
                      {publicationTestStatusSummary(status)}
                      {status.message ? ` · ${status.message}` : ''}
                    </Text>
                  ))}
                </Stack>
              )}
            </Alert>
          )}
          {!displayedTestStatus && (
            <Alert color="yellow" title="Release test status unavailable">
              Test status could not be loaded for this release.
            </Alert>
          )}
          {!fresh && (
            <Alert color="orange">
              Refresh candidate and evidence before preparing publication.
            </Alert>
          )}
          {showFreezeAction && action === 'activate' && candidate.status === 'draft' && (
            <>
              <Text size="sm">
                {proof?.can_freeze
                  ? 'Freeze this saved candidate before reviewing publication.'
                  : 'Freezing requires a current, valid release configuration. Review the findings above.'}
              </Text>
              <Button
                variant={review ? 'default' : 'filled'}
                disabled={
                  !canEdit || !fresh || !proof?.can_freeze || busy || Boolean(pending?.submitted)
                }
                loading={freeze.isPending}
                onClick={() => {
                  clearReview();
                  void freeze
                    .mutateAsync({ releaseId, revision: candidate.record.lifecycle_revision })
                    .catch((cause) => setError(errorMessage(cause)));
                }}
              >
                Freeze reviewed candidate
              </Button>
            </>
          )}
          {!selected.length && (
            <Text size="sm">Select at least one entrypoint to review publication.</Text>
          )}
          <Button
            variant={
              review || (action === 'activate' && candidate.status === 'draft')
                ? 'default'
                : 'filled'
            }
            disabled={
              !canEdit ||
              busy ||
              !fresh ||
              !selected.length ||
              Boolean(inspectionError) ||
              Boolean(pending?.submitted) ||
              (action === 'activate' &&
                candidate.status !== 'frozen' &&
                candidate.status !== 'promoted')
            }
            loading={preview.isPending}
            onClick={() => void reviewPublication()}
          >
            Review {action === 'rollback' ? 'restore' : 'publication'}
          </Button>
        </>
      )}
      {review && (
        <Stack>
          {Date.parse(review.plan.expires_at) <= now && !pending?.submitted && (
            <Alert color="orange">Review expired. Prepare a new publication review.</Alert>
          )}
          <Text>Review expires {new Date(review.plan.expires_at).toLocaleString()}</Text>
          {review.plan.targets.map((target) => (
            <Stack key={target.before.automation_id} gap="xs">
              <Text fw={600}>
                {target.roots[0] ? (
                  <ImplementationLabel version={target.roots[0]} />
                ) : (
                  `Automation ${target.before.automation_id}`
                )}
              </Text>
              <Text size="sm">
                Current Release: {target.before.published_release_id ?? 'unpublished'} · head{' '}
                {target.before.head_revision}
              </Text>
              {target.before.active_roots.map((root) => (
                <Text key={root.trigger_step_id} size="sm">
                  Current:{' '}
                  <TriggerLabel
                    versionId={root.automation_version_id}
                    triggerId={root.trigger_step_id}
                  />
                  {' · '}
                  <ImplementationLabel version={root} />
                </Text>
              ))}
              <Text size="sm">
                Proposed Release:{' '}
                {target.selected_release?.release_id ??
                  (reviewedAction === 'activate' ? releaseId : 'unpublished')}
              </Text>
              {target.roots.map((root) => (
                <Text key={root.trigger_step_id} size="sm">
                  Proposed:{' '}
                  <TriggerLabel
                    versionId={root.automation_version_id}
                    triggerId={root.trigger_step_id}
                  />
                  {' · '}
                  <ImplementationLabel version={root} />
                </Text>
              ))}
              <ExactDetails
                summary="Current and proposed bindings"
                values={Object.fromEntries([
                  ...target.before.active_roots.map((root) => [
                    `Current binding (${root.trigger_step_id})`,
                    root.binding_id,
                  ]),
                  ...target.roots.map((root) => [
                    `Proposed binding (${root.trigger_step_id})`,
                    root.binding_id,
                  ]),
                ])}
              />
            </Stack>
          ))}
          {review.plan.shared_impacts.map((impact) => {
            const target = review.plan.targets.find(
              (target) => target.before.automation_id === impact.automation_id,
            );
            return (
              <Alert key={impact.automation_id} color="orange">
                <ImplementationLabel
                  version={
                    target?.roots[0] ??
                    target?.before.active_roots[0] ?? {
                      automation_id: impact.automation_id,
                      automation_version_id: labels.get(impact.automation_id) ?? '',
                    }
                  }
                />{' '}
                is shared with Suites:{' '}
                <Group gap="xs">
                  {impact.affected_suite_ids.map((suiteId) => (
                    <SuiteLink key={suiteId} suiteId={suiteId} />
                  ))}
                </Group>
                Its entrypoint changes everywhere it is used.
              </Alert>
            );
          })}
          <ExactDetails
            summary="Publication identities"
            values={{
              'Candidate ID': review.plan.release.release_id,
              'Execution hash': review.plan.release.identity.composition_digest,
              'Resource hash': review.plan.release.identity.resource_digest,
              'Documentation hash': review.plan.release.identity.documentation_digest,
              'Requirements hash': review.plan.release.identity.requirements_digest,
            }}
          />
          {review.plan.findings.map((finding, index) => (
            <Alert key={index} color="orange">
              {finding.message}
              <Text size="sm">{finding.next_action}</Text>
            </Alert>
          ))}
          {(review.plan.contract_warnings ?? []).map((warning) => (
            <Alert
              key={`${warning.automation_version_id}:${warning.finding_key}`}
              color="yellow"
              title="Contract warning — publishing is allowed"
            >
              <Text fw={600}>
                {warning.automation_id} · {warning.step_name}
              </Text>
              <Text>{warning.message}</Text>
              <Text size="sm">{warning.next_action}</Text>
            </Alert>
          ))}
          {showDocuments &&
            confirmationDocumentFences
              .filter((fence) => fence.release_id !== releaseId)
              .map((fence) => {
                const result = documentResultByReleaseId.get(fence.release_id);
                if (result?.data)
                  return (
                    <ReleaseDocumentationReview
                      key={fence.release_id}
                      releaseId={fence.release_id}
                      documents={result.data}
                      detail={result.data}
                      showDocuments
                      loadingDocuments={result.isFetching}
                      documentsError={result.error}
                      onToggleDocuments={() => setShowDocuments(false)}
                      onRetryDocuments={() => void result.refetch()}
                    />
                  );
                if (result?.isError)
                  return (
                    <Alert
                      key={fence.release_id}
                      color="red"
                      title="Exact Release documents unavailable"
                    >
                      Documents for Release <Code>{fence.release_id}</Code> could not be loaded:{' '}
                      {errorMessage(result.error)}
                      <Button variant="subtle" onClick={() => void result.refetch()}>
                        Retry
                      </Button>
                    </Alert>
                  );
                return (
                  <Text key={fence.release_id} size="sm">
                    Loading exact Release documents for <Code>{fence.release_id}</Code>…
                  </Text>
                );
              })}
          {!pending?.submitted && review.plan.document_confirmation_required && (
            <Checkbox
              checked={acknowledged}
              disabled={busy || !documentsFresh}
              onChange={(event) => setAcknowledged(event.currentTarget.checked)}
              label="I reviewed the exact Release documents."
            />
          )}
          {!pending?.submitted && testsNeedAcknowledgement && (
            <Checkbox
              checked={testStatusAcknowledged}
              disabled={busy}
              onChange={(event) => setTestStatusAcknowledged(event.currentTarget.checked)}
              label={publicationTestStatusAcknowledgement(displayedTestStatus?.aggregate)}
            />
          )}
        </Stack>
      )}
      {pending?.submitted && (
        <Alert color="orange">
          The previous confirmation outcome is unknown. Recover the identical request; do not create
          another publication.
          <Text size="sm">
            Original action: {pending.preview.plan.action} · Automations:{' '}
            {pending.preview.plan.targets.map((target) => target.before.automation_id).join(', ')}
          </Text>
        </Alert>
      )}
      {review && !pending?.submitted && fresh && !reviewFresh && (
        <Alert color="orange">
          Candidate changed since this review. Prepare a new publication preview.
        </Alert>
      )}
      {error && <Alert color="red">{error}</Alert>}
      {success && <Alert color="green">{success}</Alert>}
      {(review || pending?.submitted) && (
        <Group>
          <Button
            loading={confirm.isPending}
            disabled={
              !canEdit ||
              busy ||
              (!pending?.submitted &&
                (!reviewFresh ||
                  hasBlockingFindings ||
                  (review?.plan.document_confirmation_required &&
                    (!acknowledged || !documentsFresh)) ||
                  (testsNeedAcknowledgement && !testStatusAcknowledged) ||
                  Date.parse(review?.plan.expires_at ?? '') <= now))
            }
            onClick={() => void submit()}
          >
            {pending?.submitted
              ? 'Recover publication result'
              : reviewedAction === 'rollback'
                ? 'Confirm restore'
                : 'Confirm publication'}
          </Button>
          {!pending?.submitted && (
            <Button variant="default" disabled={busy} onClick={clearReview}>
              Discard review
            </Button>
          )}
        </Group>
      )}
    </ReleaseSection>
  );
}
function errorMessage(cause: unknown) {
  return getHttpErrorMessage(cause);
}
function sameFence(left: Fence, right: Fence) {
  return (
    left.release_id === right.release_id &&
    left.lifecycle_revision === right.lifecycle_revision &&
    Object.entries(right.identity).every(
      ([name, value]) => left.identity[name as keyof Fence['identity']] === value,
    )
  );
}
